<template>
    <div>
      <Header />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Billing Groups</h5>
          <p class="page-sub">Manage customer groups used for batch billing</p>
        </div>
        <div class="page-actions">
  
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input type="text" class="search-input" placeholder="Search groups..." @input="tableData.page = 1; fetchData()" v-model="tableData.search" />
          </div>
  
          <select v-model="tableData.status" @change="tableData.page = 1; fetchData()" class="filter-select">
            <option :value="[1]">Active</option>
            <option :value="[0]">Inactive</option>
            <option :value="[0,1]">All</option>
          </select>
  
          <div class="dropdown">
            <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="download" /> Export
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><a class="dropdown-item" href="#" @click="drawPdf(print_content, headerArrayColumn, 'p', 'billing_groups', 'Billing Groups')">
                <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
              </a></li>
              <li><a class="dropdown-item" href="#" @click="reusableMixinToCSV('billing_groups.csv')">
                <font-awesome-icon icon="file-csv" class="me-2 text-success" /> CSV
              </a></li>
            </ul>
          </div>
  
          <button class="btn-primary-action" :disabled="permission.create_billing_group != 1" @click="openCreateModal">
            <font-awesome-icon icon="plus" /> Create Group
          </button>
  
        </div>
      </div>
  
      <!-- Data Card -->
      <div class="data-card">
  
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted-cell">Loading groups...</p>
        </div>
  
        <div v-else-if="permission_status == 403" class="state-block">
          <div class="state-icon state-icon--red"><font-awesome-icon icon="lock" /></div>
          <p class="state-title">Access Denied</p>
          <p class="state-sub">You don't have permission to view this resource.</p>
        </div>
  
        <div v-else-if="details.length === 0" class="state-block">
          <div class="state-icon state-icon--blue"><font-awesome-icon icon="layer-group" /></div>
          <p class="state-title">No billing groups</p>
          <p class="state-sub">Create a group to start managing batch billings.</p>
          <button v-if="permission.create_billing_group == 1" class="btn-primary-action mt-3" @click="openCreateModal">
            <font-awesome-icon icon="plus" /> Create Group
          </button>
        </div>
  
        <template v-else>
  
          <div class="table-toolbar">
            <span class="table-count">Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong></span>
            <div class="toolbar-right">
              <label class="table-count">Rows:</label>
              <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                <option value="5">5</option><option value="10">10</option><option value="20">20</option>
                <option value="30">30</option><option value="50">50</option><option value="100">100</option>
                <option :value="pagination.total">All</option>
              </select>
            </div>
          </div>
  
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Group Name</th>
                  <th>Status</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in details" :key="row.id">
  
                  <td class="text-muted-cell">{{ index + 1 }}</td>
  
                  <td>
                    <div class="group-cell">
                      <div class="group-avatar"><font-awesome-icon icon="layer-group" /></div>
                      <span class="group-name">{{ row.name }}</span>
                    </div>
                  </td>
  
                  <td>
                    <div class="toggle-wrap">
                      <label class="toggle">
                        <input type="checkbox" :true-value="1" :false-value="0"
                          v-model="statuses[index]"
                          @change="permission.update_billing_group == 1 ? updateGroupStatus(row.id) : null"
                          :disabled="permission.update_billing_group != 1"
                        />
                        <span class="toggle-slider"></span>
                      </label>
                      <span class="toggle-label" :class="statuses[index] == 1 ? 'toggle-label--on' : 'toggle-label--off'">
                        {{ statuses[index] == 1 ? 'Active' : 'Inactive' }}
                      </span>
                    </div>
                  </td>
  
                  <td class="col-actions">
                    <div class="action-btns">
                      <button class="action-btn action-btn--edit"
                        :disabled="permission.update_billing_group != 1"
                        @click="permission.update_billing_group == 1 ? openUpdateModal(index) : null"
                        title="Edit group">
                        <font-awesome-icon icon="pen" />
                      </button>
                      <button class="action-btn action-btn--delete"
                        :disabled="permission.delete_billing_group != 1"
                        @click="permission.delete_billing_group == 1 ? openDeleteModal(index) : null"
                        title="Delete group">
                        <font-awesome-icon icon="trash" />
                      </button>
                    </div>
                  </td>
  
                </tr>
              </tbody>
            </table>
          </div>
  
          <div class="table-footer">
            <pagination :pagination="pagination" @prev="tableData.page = pagination.currentPage - 1; fetchData()" @next="tableData.page = pagination.currentPage + 1; fetchData()" />
          </div>
  
        </template>
      </div>
  
  
      <!-- ── Create Modal ── -->
      <div class="modal fade" id="create_group" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-sm">
          <div class="modal-content modal-clean">
            <div class="modal-header-clean">
              <div class="modal-title-icon" style="background:rgba(0,74,173,.1);color:#004AAD;">
                <font-awesome-icon icon="layer-group" />
              </div>
              <div><h6 class="modal-clean-title">Create Billing Group</h6><p class="modal-clean-sub">Add a new customer group</p></div>
              <button type="button" class="btn-close ms-auto" data-bs-dismiss="modal"></button>
            </div>
            <form @submit.prevent="createGroup">
              <div class="modal-body">
                <div class="field-group">
                  <label class="field-label">Group Name <span class="req">*</span></label>
                  <input type="text" class="field-input" placeholder="e.g. Premium Customers" v-model="name" />
                </div>
              </div>
              <div class="modal-footer border-0 pt-0">
                <button type="button" class="btn-outline" data-bs-dismiss="modal">Cancel</button>
                <button type="submit" class="btn-primary-action" :disabled="loading || !name?.trim()">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  {{ loading ? 'Creating...' : 'Create Group' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
  
  
      <!-- ── Update Modal ── -->
      <div class="modal fade" id="update_modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-sm">
          <div class="modal-content modal-clean">
            <div class="modal-header-clean">
              <div class="modal-title-icon" style="background:rgba(0,74,173,.1);color:#004AAD;">
                <font-awesome-icon icon="pen" />
              </div>
              <div><h6 class="modal-clean-title">Update Group</h6><p class="modal-clean-sub">{{ currentData.name }}</p></div>
              <button type="button" class="btn-close ms-auto" @click="closeUpdateModal"></button>
            </div>
            <form @submit.prevent="updateGroup">
              <div class="modal-body">
                <div class="field-group">
                  <label class="field-label">Group Name <span class="req">*</span></label>
                  <input type="text" class="field-input" placeholder="Enter group name" v-model="currentData.name" />
                </div>
              </div>
              <div class="modal-footer border-0 pt-0">
                <button type="button" class="btn-outline" @click="closeUpdateModal">Cancel</button>
                <button type="submit" class="btn-primary-action" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  {{ loading ? 'Saving...' : 'Update Group' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
  
  
      <!-- ── Delete Modal ── -->
      <div class="modal fade" id="delete_modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-sm">
          <div class="modal-content modal-clean">
            <div class="modal-icon-wrap">
              <div class="modal-icon modal-icon--red"><font-awesome-icon icon="trash" /></div>
            </div>
            <div class="modal-body-clean">
              <p class="modal-heading">Delete Group?</p>
              <p class="modal-subtext">
                Are you sure you want to delete <strong>{{ currentData.name }}</strong>?
                This action cannot be undone.
              </p>
            </div>
            <div class="modal-actions">
              <button type="button" class="modal-btn modal-btn--cancel" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="modal-btn modal-btn--red" data-bs-dismiss="modal" @click="deleteGroup">
                <font-awesome-icon icon="trash" class="me-1" /> Delete
              </button>
            </div>
          </div>
        </div>
      </div>
  
    </div>
  </template>
  
  <script>
  import { reusableMixin } from '../../../lib/reusableMixin';
  import Header from './component/header.vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { useStore } from 'vuex';
  
  export default {
    components: { Header },
    mixins: [reusableMixin],
  
    data() {
      return {
        name:             '',
        print_content:    [],
        headerArrayColumn: ['S/N','Group Name'],
        details:          [],
        statuses:         [],
        loading:          false,
        currentData:      {},
        permission_status: null,
  
        tableData: { page: 1, draw:0, length:10, search:'', column:0, dir:'desc', id:'', status:[0,1] },
        pagination: { lastPage:'', currentPage:'', total:'', lastPageUrl:'', nextPageUrl:'', prevPageUrl:'', from:'', to:'' },
        toast:  useToast(),
        store:  useStore(),
      };
    },
  
    computed: {
      permission() { return this.store.state.getCurrentUserPermission; },
    },
  
    created() { this.fetchData(); },
  
    methods: {
      dataToPrint(details) {
        this.print_content = details.map((d, idx) => ({ sn: idx + 1, name: d.name }));
      },
  
      fetchData(url = '/billing/group/all') {
        this.loading = true;
        this.tableData.draw++;
        axios.get(url, { params: this.tableData })
          .then(response => {
            this.details  = response.data.data.data;
            this.statuses = this.details.map(c => c.status);
            this.dataToPrint(this.details);
            this.configPagination(response.data.data);
            this.loading = false;
          })
          .catch(error => {
            this.loading = false;
            if (error?.response?.status == 403) { this.permission_status = 403; this.toast.error(error.response.data.message); }
          });
      },
  
      openCreateModal()  { window.$('#create_group').modal('show'); },
      closeUpdateModal() { window.$('#update_modal').modal('hide'); },
  
      async createGroup() {
        if (!this.name?.trim()) return;
        this.loading = true;
        axios.post('/billing/group/create', { name: this.name })
          .then(() => {
            this.loading = false;
            this.toast.success('Billing group created!');
            this.name = ''; this.fetchData();
            window.$('#create_group').modal('hide');
          })
          .catch(error => {
            this.loading = false;
            this.toast.warning(error.response?.data?.message ?? 'Something went wrong');
          });
      },
  
      async updateGroup() {
        this.loading = true;
        axios.put('/billing/group/update', this.currentData)
          .then(() => {
            this.loading = false;
            this.toast.success('Group updated!');
            this.fetchData(); this.closeUpdateModal();
          })
          .catch(error => {
            this.loading = false;
            this.toast.error(error.response?.data?.message ?? 'Something went wrong');
          });
      },
  
      async updateGroupStatus(id) {
        axios.put(`billing/group/update-status/${id}`)
          .then(res => { this.toast.success(res.data.message); this.fetchData(); })
          .catch(error => { this.toast.error(error.response?.data?.message ?? 'Status update failed'); });
      },
  
      async deleteGroup() {
        axios.delete(`/billing/group/delete/${this.currentData.id}`)
          .then(() => { this.toast.success('Group deleted!'); this.fetchData(); })
          .catch(error => { this.toast.error(error.response?.data?.message ?? 'Group could not be deleted'); });
      },
  
      openUpdateModal(index) { this.currentData = { ...this.details[index] }; window.$('#update_modal').modal('show'); },
      openDeleteModal(index) { this.currentData = this.details[index]; window.$('#delete_modal').modal('show'); },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
    },
  };
  </script>
  
  <style scoped>
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:200px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.5; cursor:not-allowed; }
  .btn-outline  { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-outline:hover { background:#f4f7fb; }
  .btn-outline::after { display:none !important; }
  .me-2 { margin-right:8px; } .mt-3 { margin-top:12px; } .ms-auto { margin-left:auto; } .me-1 { margin-right:4px; }
  
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  .state-block  { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon   { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
  .state-icon--red  { background:rgba(224,53,53,.1); color:#e03535; }
  .state-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub    { font-size:13px; color:#7a90b0; margin:0; }
  .text-muted-cell { color:#4a5e78; }
  
  .table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count   { font-size:13px; color:#7a90b0; }
  .toolbar-right { display:flex; align-items:center; gap:8px; }
  .rows-select   { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }
  
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 14px; border-bottom:1px solid #e4eaf2; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:12px 14px; font-size:13px; vertical-align:middle; }
  .col-actions { width:80px; }
  
  /* ── Group Cell ── */
  .group-cell   { display:flex; align-items:center; gap:10px; }
  .group-avatar { width:34px; height:34px; border-radius:9px; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:13px; flex-shrink:0; }
  .group-name   { font-size:13.5px; font-weight:600; color:#1a2b45; }
  
  /* ── Toggle ── */
  .toggle-wrap  { display:flex; align-items:center; gap:8px; }
  .toggle       { position:relative; display:inline-block; width:36px; height:20px; flex-shrink:0; }
  .toggle input { opacity:0; width:0; height:0; }
  .toggle-slider { position:absolute; cursor:pointer; inset:0; background:#dde5f0; border-radius:20px; transition:background .2s; }
  .toggle-slider::before { content:''; position:absolute; width:14px; height:14px; left:3px; bottom:3px; background:#fff; border-radius:50%; transition:transform .2s; box-shadow:0 1px 3px rgba(0,0,0,.15); }
  .toggle input:checked + .toggle-slider { background:#004AAD; }
  .toggle input:checked + .toggle-slider::before { transform:translateX(16px); }
  .toggle input:disabled + .toggle-slider { opacity:.5; cursor:not-allowed; }
  .toggle-label      { font-size:12px; font-weight:600; }
  .toggle-label--on  { color:#16a34a; }
  .toggle-label--off { color:#7a90b0; }
  
  /* ── Action Buttons ── */
  .action-btns  { display:flex; align-items:center; gap:5px; }
  .action-btn   { width:28px; height:28px; border-radius:7px; border:1px solid #e4eaf2; display:flex; align-items:center; justify-content:center; font-size:11px; cursor:pointer; transition:background .15s; background:#fff; }
  .action-btn:disabled { opacity:.4; cursor:not-allowed; }
  .action-btn--edit   { color:#004AAD; } .action-btn--edit:hover:not(:disabled)   { background:#edf4ff; border-color:#c5d6f5; }
  .action-btn--delete { color:#e03535; } .action-btn--delete:hover:not(:disabled) { background:#fff0f0; border-color:#fac5c5; }
  
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  
  .dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:160px; }
  .dropdown-item { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; text-decoration:none; display:block; }
  .dropdown-item:hover { background:#f4f7fb; }
  
  /* ── Modals ── */
  .modal-clean { border:none; border-radius:16px; box-shadow:0 20px 60px rgba(0,0,0,.14); overflow:hidden; }
  .modal-header-clean { display:flex; align-items:flex-start; gap:14px; padding:20px 24px 16px; border-bottom:1px solid #f0f4f8; }
  .modal-title-icon { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
  .modal-clean-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .modal-clean-sub   { font-size:12px; color:#7a90b0; margin:0; }
  .field-group  { display:flex; flex-direction:column; gap:6px; }
  .field-label  { font-size:13px; font-weight:600; color:#1a2b45; }
  .req          { color:#e03535; }
  .field-input  { padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; font-family:inherit; }
  .field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  .modal-icon-wrap { display:flex; justify-content:center; padding:28px 24px 0; }
  .modal-icon { width:60px; height:60px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:26px; }
  .modal-icon--red { background:rgba(224,53,53,.1); color:#e03535; }
  .modal-body-clean { padding:16px 28px 8px; text-align:center; }
  .modal-heading    { font-size:17px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
  .modal-subtext    { font-size:13.5px; color:#7a90b0; margin:0; line-height:1.6; }
  .modal-actions    { display:flex; gap:10px; padding:18px 24px 24px; justify-content:center; }
  .modal-btn        { display:inline-flex; align-items:center; justify-content:center; font-size:13px; font-weight:600; padding:9px 22px; border-radius:8px; border:none; cursor:pointer; flex:1; }
  .modal-btn--cancel { background:#f4f7fb; color:#1a2b45; border:1px solid #dde5f0; }
  .modal-btn--blue   { background:#004AAD; color:#fff; }
  .modal-btn--red    { background:#e03535; color:#fff; } .modal-btn--red:hover { background:#c42b2b; }
  
  @media(max-width:991px) { .page-header,.page-actions { flex-direction:column; align-items:stretch; } .search-input { width:100%; } }
  </style>